import { createSlice, createAction } from '@reduxjs/toolkit';
import {getValuesByPaths} from "../utils";
import {
    deleteUserAppData, saveUserAppData, updateUser, fetchUserFields, createProspect, updateProspectPortfolios,
    createPage, updatePage, deletePages, setUserFields, createTouchpoint, updateTouchpoint, deleteTouchpoint,
    fetchTouchpoint, listPageViews, createOrUpdatePortfolioGroup
} from "../../api/userApi";
import { createPortfolioPath, updatePortfolioPath, removePortfolioPath } from "../../api/pathApi";
import { listFiles, uploadFiles, renameFile, renameFolder, deleteResources } from "../../api/filesApi";
import { fetchUpcomingEvents } from "../../api/tickersApi";
import {
    addPortfoliosToCompany, updateCompanyPortfolio,
    createPathPage, updatePathPage, deletePathPage,
    updateCompanyActivePathIds, updateCompanyFields
} from "../../api/companyApi";


const handleCreateFulfilled = (state, action, dataKey) => {
  state.status = 'succeeded';
  if (!state.data[dataKey]) {
    state.data[dataKey] = [action.payload];
  } else {
    state.data[dataKey].push(action.payload);
  }
};



const userSlice = createSlice({
  name: 'user',
  initialState: {
    data: null, // Includes authentication details and app_data
    files: null,
    filesLoaded: false,
    upcomingEvents: null,
    status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
    error: null,

  },
  reducers: {
      setUserData: (state, action) => {
          state.data = {...action.payload};
      },
  },
  extraReducers: (builder) => {
      builder
          .addCase(createProspect.fulfilled, (state, action) => {
              handleCreateFulfilled(state, action, 'prospects');
          })
          .addCase(createPage.fulfilled, (state, action) => {
              handleCreateFulfilled(state, action, 'page_details');
          })
          .addCase(createTouchpoint.fulfilled, (state, action) => {
              handleCreateFulfilled(state, action, 'touchpoint_details');
          })
          .addCase(updateUser.fulfilled, (state, action) => {
              const {responseData, updateData} = action.payload;
              state.status = 'succeeded';
          })
          .addCase(setUserFields.fulfilled, (state, action) => {
              state.status = 'succeeded';
              state.data = {
                  ...state.data,
                  ...action.payload
              }
          })

        .addCase(saveUserAppData.fulfilled, (state, action) => {
            state.status = 'succeeded';
            const { id, data } = action.payload;
            if (!id) {
                // This is a new app data entry and data is nested under the 'data' key
                state.data.app_data = [...state.data.app_data, data?.data];
            } else {
                // This is an update to an existing app data entry
                state.data.app_data = state.data.app_data.map(item =>
                    item.id === id ? { ...item, data: data } : item
                );
            }
        })
        .addCase(deleteUserAppData.fulfilled, (state, action) => {
            state.status = 'succeeded';
            state.data.app_data = state.data.app_data.filter(item => !action.payload?.ids?.includes?.(item.id));
        })
        .addCase(fetchUserFields.fulfilled, (state, action) => {
          if (action.payload) {
            state.data = {
              ...state.data,
              ...action.payload,
            };
          }
    })
        .addCase(updateTouchpoint.fulfilled, (state, action) => {
            state.status = 'succeeded';
            const index = state.data.touchpoint_details.findIndex(
              (page) => page.id === action.payload.touchpointId
            );
            if (index !== -1) {
              state.data.touchpoint_details[index] = {id: action.payload.touchpointId, ...action.payload.touchpointData}
            }
        })
        .addCase(fetchTouchpoint.fulfilled, (state, action) => {
            state.status = 'succeeded';
            const touchpointId = action.meta.arg;  // Assuming the touchpoint ID is passed as the argument
            const index = state.data.touchpoint_details.findIndex(tp => tp.id === touchpointId);
            if (index !== -1) {
                // If the touchpoint is found, update its content
                state.data.touchpoint_details[index].content = action.payload;
            } else {
                // Otherwise, add new touchpoint detail with content
                state.data.touchpoint_details.push({
                    id: touchpointId,
                    content: action.payload
                });
            }
          })
        .addCase(updatePage.fulfilled, (state, action) => {
            state.status = 'succeeded';
            const index = state.data.page_details.findIndex(
              (page) => page.page_id === action.payload.pageId
            );
            if (index !== -1) {
              state.data.page_details[index] = {page_id: action.payload.pageId, ...action.payload.pageData}
            }
        })
        .addCase(deletePages.fulfilled, (state, action) => {
            state.status = 'succeeded';
            state.data.page_details = state.data.page_details.filter(page => !action.payload.includes(page.page_id));
        })
        .addCase(deleteTouchpoint.fulfilled, (state, action) => {
            state.status = 'succeeded';
            state.data.touchpoint_details = state.data.touchpoint_details.filter(touchpoint => touchpoint.id !== action.payload);
        })
        .addCase(createProspect.rejected, (state, action) => {
          state.status = 'failed';
          state.error = action.payload;
        })
        .addCase(updateProspectPortfolios.fulfilled, (state, action) => {
            // Assuming you have a way to find and update the specific prospect within your state
            const index = state.data.prospects.findIndex(prospect => prospect.userId === action.payload.prospectUserId);
            if (index !== -1) {
                state.data.prospects[index].portfolios = action.payload.portfolios;
            }
        })
        .addCase(listFiles.fulfilled, (state, action) => {
            state.status = 'succeeded';
            state.files = [...(state.files || []), ...action.payload];
            state.filesLoaded = true; // Set filesLoaded to true

        })
        .addCase(listPageViews.fulfilled, (state, action) => {
          state.status = 'succeeded';

          const { pageId, pageViews } = action.payload;

          // Find the page with the matching pageId
          const pageIndex = state.data.page_details.findIndex(page => page.page_id === pageId);

          if (pageIndex !== -1) {
            // If the page is found, append the pageViews
            state.data.page_details[pageIndex].pageViews = pageViews;
          } else {
            // If the page is not found, create a new entry
            state.data.page_details.push({
              page_id: pageId,
              pageViews,
            });
          }
        })
        .addCase(uploadFiles.pending, (state) => {
            state.status = 'loading';
        })
        .addCase(uploadFiles.fulfilled, (state, action) => {
            state.status = 'succeeded';
            state.files = [...(state.files || []), ...action.payload];
        })
        .addCase(uploadFiles.rejected, (state, action) => {
            state.status = 'failed';
            state.error = action.payload;
        })
        .addCase(fetchUpcomingEvents.fulfilled, (state, action)=>{
            state.status = 'succeeded';
            state.upcomingEvents = action.payload;
        })
        .addCase(renameFile.fulfilled, (state, action) => {
          const fileIndex = state.files.findIndex(file => file.path === action.payload.oldPath);

          // Check if the file was found
          if (fileIndex !== -1) {
            // Update the path of the file to the new path
            state.files[fileIndex] = {
              ...state.files[fileIndex],
              path: `${action.payload.newPath}.${state.files[fileIndex].ext}`
            };
          }
      })
      .addCase(renameFile.rejected, (state, action) => {
      })
      .addCase(renameFolder.fulfilled, (state, action) => {
      })
      .addCase(renameFolder.rejected, (state, action) => {
      })
      .addCase(createPortfolioPath.fulfilled, (state, action) => {
          state.status = 'succeeded';
          if (!state.data.path_details) {
            state.data.path_details = [action.payload];
          } else {
            state.data.path_details.push(action.payload);
          }
    })
    .addCase(createPortfolioPath.rejected, (state, action) => {
      state.status = 'failed';
      state.error = action.payload;
    })

    // Handle updating an existing portfolio path
    .addCase(updatePortfolioPath.fulfilled, (state, action) => {
      state.status = 'succeeded';

      const { pathId, data } = action.payload; // Extract pathId and updated data
      const index = state.data.path_details.findIndex((path) => path.id === pathId);

      if (index !== -1) {
        // Replace the existing path with the updated data
        state.data.path_details[index] = { ...state.data.path_details[index], ...data };
      }
    })
    .addCase(updatePortfolioPath.rejected, (state, action) => {
      state.status = 'failed';
      state.error = action.payload;
    })

    // Handle removing a portfolio path
    .addCase(removePortfolioPath.fulfilled, (state, action) => {
      state.status = 'succeeded';
      const pathId = action.meta.arg.pathId;
      state.data.path_details = state.data.path_details.filter(
        (path) => path.id !== pathId
      );
    })
    .addCase(removePortfolioPath.rejected, (state, action) => {
      state.status = 'failed';
      state.error = action.payload;
    })
    .addCase(addPortfoliosToCompany.fulfilled, (state, action) => {
        const portfolios = action.payload; // Expecting array of CompanyPortfolio objects

        // Initialize company_details if it doesn't exist
        if (!state.data.company_details) {
            state.data.company_details = { portfolios: [] };
        }

        // Initialize portfolios if it doesn't exist
        if (!state.data.company_details.portfolios) {
            state.data.company_details.portfolios = [];
        }

        // Add only unique portfolio objects based on ID
        const existingPortfolios = new Map(
            state.data.company_details.portfolios.map(p => [p.id, p])
        );

        portfolios.forEach(newPortfolio => {
            existingPortfolios.set(newPortfolio.id, {
                id: newPortfolio.id,
                max_weight: newPortfolio.max_weight ?? 1.0 // Use default if not provided
            });
        });

        state.data.company_details.portfolios = Array.from(existingPortfolios.values());

        // Maintain backwards compatibility with portfolio_ids if needed
        state.data.company_details.portfolio_ids = Array.from(existingPortfolios.keys());

        state.status = 'succeeded';
    })
    .addCase(addPortfoliosToCompany.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
    })
    .addCase(updateCompanyPortfolio.fulfilled, (state, action) => {
        const { portfolioId, ...updateFields } = action.payload;

        // Make sure company_details and portfolios exist
        if (!state.data.company_details?.portfolios) {
            return;
        }

        // Find and update the specific portfolio
        state.data.company_details.portfolios = state.data.company_details.portfolios.map(portfolio => {
            if (portfolio.id === portfolioId) {
                return {
                    ...portfolio,
                    ...updateFields
                };
            }
            return portfolio;
        });

        state.status = 'succeeded';
    })
    .addCase(updateCompanyActivePathIds.fulfilled, (state, action) => {
        const { pathIds } = action.payload;
        state.data.company_details.active_path_ids = pathIds;
    })
    .addCase(updateCompanyPortfolio.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
    })
    .addCase(createPathPage.fulfilled, (state, action) => {
        state.status = 'succeeded';
        if (!state.data.company_details.path_pages) {
            state.data.company_details.path_pages = [action.payload];
        } else {
            state.data.company_details.path_pages.push(action.payload);
        }
    })
    .addCase(updatePathPage.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const { pageId, ...updateFields } = action.payload;

        state.data.company_details.path_pages = state.data.company_details.path_pages.map(page =>
            page.id === pageId ? { ...page, ...updateFields } : page
        );
    })
    .addCase(deletePathPage.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.data.company_details.path_pages = state.data.company_details.path_pages.filter(
            page => page.id !== action.payload
        );
    })
    .addCase(updateCompanyFields.fulfilled, (state, action) => {
        const { companyId, updateFields } = action.payload;

        // Ensure company_details exists in the state
        if (!state.data.company_details) {
            state.data.company_details = {};
        }

        // Update the fields in company_details dynamically
        for (const [key, value] of Object.entries(updateFields)) {
            state.data.company_details[key] = value;
        }
    })
    .addCase(createOrUpdatePortfolioGroup.fulfilled, (state, action) => {
        state.status = 'succeeded';

        // Initialize portfolio_groups if it doesn't exist
        if (!state.data.portfolio_groups) {
            state.data.portfolio_groups = [];
        }

        const { group_id, portfolio_ids, name } = action.payload;

        // Find if group already exists
        const groupIndex = state.data.portfolio_groups.findIndex(
            group => group.group_id === group_id
        );

        if (groupIndex !== -1) {
            // Update existing group
            state.data.portfolio_groups[groupIndex] = {
                group_id,
                portfolio_ids,
                name
            };
        } else {
            // Add new group
            state.data.portfolio_groups.push({
                group_id,
                portfolio_ids,
                name
            });
        }
    })
  },
});


export const setDataLoadingStatus = createAction('user/setDataLoadingStatus');




export const selectAppDataByType = (state, appType) => {
  return state.user.data?.app_data.filter(item => item.app_type === appType) || [];
};

export const selectAppDataById = (state, id) => {
    return state.user?.data?.app_data.find(item => item.id === id);
}


export const selectUserFields = (state, paths) => {
  return getValuesByPaths(state.user.data, paths);
};


export const { setUserData } = userSlice.actions;
export default userSlice.reducer;
